// Vertical scrolling map renderer (Slay-the-Spire style portrait layout).
// Layer 0 sits at the top; the boss at the bottom.

import { LAYER_COUNT, championIconUrl } from "../config/constants";
import { NODE_TYPES } from "../data/nodes";
import { CampaignMap, type MapNode } from "./CampaignMap";
import { nodeImagePath, nodeShortLabel } from "./MapGenerator";
import { jungleCampXp, MINION_XP } from "../scenes/RewardScene";
import { CHAMPIONS, type ChampionTier } from "../data/champions";
import { levelForXp, effectiveStatsForLevel } from "../units/UnitStats";
import type { RosterEntry, StarLevel } from "../game/RunState";

function getStarMult(tier: ChampionTier, stars: StarLevel): number {
  const mults: Record<ChampionTier, [number, number, number, number]> = {
    common:    [1, 1.5, 2.0, 3.0],
    rare:      [1, 1.7, 2.6, 4.0],
    epic:      [1, 2.0, 3.2, 5.0],
    legendary: [1, 2.0, 3.8, 6.0],
  };
  return (mults[tier] ?? mults.common)[stars - 1] ?? 1;
}

function rosterMaxHp(entry: RosterEntry): number {
  const champ = CHAMPIONS[entry.championId];
  const level = levelForXp(entry.xp);
  const sm = getStarMult(champ.tier, entry.stars ?? 1);
  return effectiveStatsForLevel(
    { ...champ, baseHp: Math.round(champ.baseHp * sm), baseAd: Math.round(champ.baseAd * sm), baseAp: Math.round(champ.baseAp * sm) },
    level,
    champ.levelGrowth,
  ).maxHp;
}

export interface MapRendererCallbacks {
  onNodeClick: (node: MapNode) => void;
  onOpenHub: () => void;
  onOpenAchievements?: () => void;
  onSlotSwap?: (idA: string, idB: string) => void;
  onRequestReturnToMenu?: () => void;
}

const TOP_PADDING    = 36;
// Bottom padding is sized to clear the persistent ad anchor (90px desktop /
// 50px mobile) so the boss node and its label don't sit under the banner.
const BOTTOM_PADDING = 130;
const MIN_SPACING    = 76;
const MAX_SPACING    = 120;

const NODE_SCALE: Partial<Record<string, number>> = {
  boss: 2.2,
};

export class MapRenderer {
  private root:      HTMLDivElement;
  private scroll:    HTMLDivElement;
  private board:     HTMLDivElement;
  private rosterDiv: HTMLDivElement;
  private resizeObserver: ResizeObserver | null = null;
  private dragId: string | null = null;

  constructor(
    private map:      CampaignMap,
    private actLabel: string,
    private actSub:   string,
    private callbacks: MapRendererCallbacks,
    private roster:   RosterEntry[] = [],
    backgroundUrl?: string,
    private region?: string,
    themeClass = "",
  ) {
    this.root = document.createElement("div");
    this.root.className = themeClass ? `map-screen ${themeClass}` : "map-screen";
    if (backgroundUrl) {
      const bg = document.createElement("div");
      bg.className = "map-bg-blur";
      bg.style.backgroundImage = `url('${backgroundUrl}')`;
      this.root.appendChild(bg);
    }

    const header = document.createElement("div");
    header.className = "map-header";

    const currentNode = this.map.currentNodeId
      ? this.map.nodes.find(n => n.id === this.map.currentNodeId)
      : null;
    const nodeSub = currentNode ? `Last: ${nodeShortLabel(currentNode)}` : this.actSub;

    const leftDiv = document.createElement("div");
    leftDiv.innerHTML = `
      <div class="act-label">${this.actLabel}</div>
      <div class="act-sub">${nodeSub}</div>
    `;
    header.appendChild(leftDiv);

    this.rosterDiv = document.createElement("div");
    this.rosterDiv.className = "map-header-roster";
    this.buildRosterIcons();
    header.appendChild(this.rosterDiv);

    const navBtns = document.createElement("div");
    navBtns.style.cssText = "display:flex;align-items:center;gap:6px;flex-shrink:0;";

    const hubBtn = document.createElement("button");
    hubBtn.className = "btn btn-sm ghost";
    hubBtn.textContent = "TEAM";
    hubBtn.addEventListener("click", () => this.callbacks.onOpenHub());
    navBtns.appendChild(hubBtn);

    const achBtn = document.createElement("button");
    achBtn.className = "btn btn-sm ghost";
    achBtn.textContent = "ACHIEVEMENTS";
    achBtn.addEventListener("click", () => this.callbacks.onOpenAchievements?.());
    navBtns.appendChild(achBtn);

    header.appendChild(navBtns);
    this.root.appendChild(header);

    this.scroll = document.createElement("div");
    this.scroll.className = "map-scroll";
    this.board = document.createElement("div");
    this.board.className = "map-board";
    this.scroll.appendChild(this.board);
    this.root.appendChild(this.scroll);
    // render() called in mount() after element has real layout dimensions.
  }

  refreshRoster(roster: RosterEntry[]): void {
    this.roster = roster;
    this.buildRosterIcons();
  }

  private buildRosterIcons(): void {
    this.rosterDiv.innerHTML = "";
    const sorted = [...this.roster].sort((a, b) => b.pos.q - a.pos.q);

    // "BACK" label on the left
    const backLabel = document.createElement("div");
    backLabel.style.cssText =
      "font-size:9px;color:rgba(180,190,210,0.5);letter-spacing:1px;align-self:center;margin-right:2px;";
    backLabel.textContent = "BACK";
    this.rosterDiv.appendChild(backLabel);

    for (const entry of sorted) {
      const champ = CHAMPIONS[entry.championId];
      const level = levelForXp(entry.xp);
      const unit = document.createElement("div");
      unit.className = "map-runit";
      unit.draggable = true;
      unit.dataset.rosterId = entry.id;
      unit.title = `${champ.displayName} Lv${level} · slot ${entry.pos.q + 1} — drag to reorder`;

      const icon = document.createElement("div");
      icon.className = "map-runit-icon";
      icon.style.backgroundImage = `url('${championIconUrl(champ.iconId)}')`;
      const stars = entry.stars ?? 1;
      const lvl = document.createElement("div");
      lvl.className = "map-runit-level";
      lvl.textContent = stars > 1 ? `Lv${level} ${"★".repeat(stars)}` : `${level}`;

      const maxHp = rosterMaxHp(entry);
      const curHp = entry.currentHp ?? maxHp;
      const ratio = Math.max(0, Math.min(1, curHp / maxHp));
      const hpBar = document.createElement("div");
      hpBar.style.cssText =
        "width:28px;height:3px;background:#1a2030;border-radius:2px;margin-top:1px;overflow:hidden;";
      const hpFill = document.createElement("div");
      const hpColor = ratio > 0.5 ? "#3da35a" : ratio > 0.25 ? "#c87a3d" : "#c8413d";
      hpFill.style.cssText = `height:100%;width:${Math.round(ratio * 100)}%;background:${hpColor};border-radius:2px;`;
      hpBar.appendChild(hpFill);

      if (entry.redBuff || entry.blueBuff) {
        const buffRow = document.createElement("div");
        buffRow.style.cssText = "display:flex;gap:3px;justify-content:center;margin-top:2px;";
        if (entry.blueBuff) {
          const dot = document.createElement("div");
          dot.style.cssText = "width:6px;height:6px;border-radius:50%;background:#3388ff;box-shadow:0 0 4px #3388ff;";
          buffRow.appendChild(dot);
        }
        if (entry.redBuff) {
          const dot = document.createElement("div");
          dot.style.cssText = "width:5px;height:5px;border-radius:50%;background:#ff5522;box-shadow:0 0 4px #ff5522;align-self:center;";
          buffRow.appendChild(dot);
        }
        unit.appendChild(buffRow);
      }

      unit.append(icon, lvl, hpBar);

      unit.addEventListener("dragstart", (e) => {
        this.dragId = entry.id;
        e.dataTransfer!.effectAllowed = "move";
        unit.style.opacity = "0.4";
      });
      unit.addEventListener("dragend", () => {
        this.dragId = null;
        unit.style.opacity = "";
      });
      unit.addEventListener("dragover", (e) => {
        e.preventDefault();
        e.dataTransfer!.dropEffect = "move";
        unit.style.outline = "1px solid rgba(200,200,200,0.4)";
      });
      unit.addEventListener("dragleave", () => {
        unit.style.outline = "";
      });
      unit.addEventListener("drop", (e) => {
        e.preventDefault();
        unit.style.outline = "";
        const fromId = this.dragId;
        const toId   = entry.id;
        if (fromId && fromId !== toId && this.callbacks.onSlotSwap) {
          this.callbacks.onSlotSwap(fromId, toId);
        }
      });

      this.rosterDiv.appendChild(unit);
    }

    const frontLabel = document.createElement("div");
    frontLabel.style.cssText =
      "font-size:9px;color:rgba(180,190,210,0.5);letter-spacing:1px;align-self:center;margin-left:2px;";
    frontLabel.textContent = "FRONT";
    this.rosterDiv.appendChild(frontLabel);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.root);
    requestAnimationFrame(() => {
      this.render();
      this.scrollToCurrent();
    });
    this.resizeObserver = new ResizeObserver(() => this.render());
    this.resizeObserver.observe(this.scroll);
  }

  unmount(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.root.remove();
  }

  refresh(): void {
    this.render();
  }

  private render(): void {
    this.board.innerHTML = "";

    const boardWidth  = this.scroll.clientWidth  || 400;
    const viewportH   = this.scroll.clientHeight || 500;
    const gaps        = LAYER_COUNT - 1;
    const rawSpacing  = (viewportH - TOP_PADDING - BOTTOM_PADDING) / gaps;
    const spacing     = Math.min(MAX_SPACING, Math.max(MIN_SPACING, rawSpacing));
    const boardHeight = Math.max(viewportH, TOP_PADDING + BOTTOM_PADDING + gaps * spacing);
    this.board.style.height = `${boardHeight}px`;
    this.board.style.width  = "100%";

    const layerPixelY = (layer: number): number =>
      TOP_PADDING + layer * spacing;

    const available = new Set(this.map.availableNodes().map((n) => n.id));
    const completed = this.map.completedNodeIds;

    // SVG layer for edges — drawn under nodes. Curved energy paths with glow.
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width",  String(boardWidth));
    svg.setAttribute("height", String(boardHeight));
    svg.style.cssText = "position:absolute;top:0;left:0;pointer-events:none;overflow:visible";

    // <defs> with a soft glow filter shared by all edges.
    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    defs.innerHTML = `
      <filter id="edge-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="3" result="blur"/>
        <feMerge>
          <feMergeNode in="blur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    `;
    svg.appendChild(defs);

    const curvePath = (ax: number, ay: number, bx: number, by: number): string => {
      // Smooth S-curve between layers — control points pulled vertically
      // toward the midpoint so straight-down edges stay straight, and angled
      // edges arc gracefully across the void.
      const midY = (ay + by) / 2;
      return `M ${ax} ${ay} C ${ax} ${midY}, ${bx} ${midY}, ${bx} ${by}`;
    };

    for (const edge of this.map.edges) {
      const a = this.map.getNode(edge.from);
      const b = this.map.getNode(edge.to);
      if (!a || !b) continue;

      const ax = a.x * boardWidth;
      const ay = layerPixelY(a.layer);
      const bx = b.x * boardWidth;
      const by = layerPixelY(b.layer);

      const aCompleted = completed.has(a.id);
      const bCompleted = completed.has(b.id);
      const isLive     = aCompleted && available.has(b.id) && !bCompleted;
      const isWalked   = aCompleted && bCompleted;

      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", curvePath(ax, ay, bx, by));
      path.setAttribute("fill", "none");

      if (isLive) {
        // Glow underlay
        const glow = document.createElementNS("http://www.w3.org/2000/svg", "path");
        glow.setAttribute("d", curvePath(ax, ay, bx, by));
        glow.setAttribute("class", "map-svg-edge live-glow");
        glow.setAttribute("fill", "none");
        svg.appendChild(glow);
        path.setAttribute("class", "map-svg-edge live");
      } else if (isWalked) {
        path.setAttribute("class", "map-svg-edge walked");
      } else {
        path.setAttribute("class", "map-svg-edge future");
      }

      svg.appendChild(path);
    }
    this.board.appendChild(svg);

    // Layer labels.
    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      const label = document.createElement("div");
      label.className = "map-layer-label";
      label.textContent = layer === LAYER_COUNT - 1 ? "BOSS" : `L${layer + 1}`;
      label.style.top = `${layerPixelY(layer)}px`;
      this.board.appendChild(label);
    }

    // Nodes — drawn on top of edges.
    for (const node of this.map.nodes) {
      const el = document.createElement("div");
      el.className = "map-node";
      el.dataset.type = node.type;
      if (node.type === "start")                                       el.classList.add("start-node");
      if (completed.has(node.id))                                      el.classList.add("done");
      if (node.id === this.map.currentNodeId && !completed.has(node.id)) el.classList.add("current");
      if (available.has(node.id) && !completed.has(node.id))          el.classList.add("active");
      const scale = NODE_SCALE[node.type] ?? 1.0;
      el.style.left      = `${node.x * boardWidth}px`;
      el.style.top       = `${layerPixelY(node.layer)}px`;
      el.style.transform = `translate(-50%, -50%) scale(${scale})`;
      // Stagger the floating animation so islands drift independently.
      const delay = ((node.layer * 0.43) + (node.indexInLayer * 0.71)) % 4;
      el.style.setProperty("--float-delay", `-${delay.toFixed(2)}s`);

      const imgPath = nodeImagePath(node, this.region);
      const label   = nodeShortLabel(node);
      const desc    = node.type === "jungle"
        ? `Gives ${jungleCampXp(node.campType)} XP to each team member.`
        : node.type === "minion"
        ? `Gives ${MINION_XP} XP to one team member.`
        : NODE_TYPES[node.type].description;
      if (imgPath) {
        el.innerHTML = `<div class="node-circle"><img class="node-img" src="${imgPath}" alt="${label}"></div><span class="node-label">${label}</span>`;
      } else {
        el.innerHTML = `<div class="node-circle"><span class="node-name">${label}</span></div><span class="node-label">${label}</span>`;
      }

      const tooltip = document.createElement("div");
      tooltip.className = "node-tooltip";
      if (node.x < 0.25)      tooltip.classList.add("tooltip-left");
      else if (node.x > 0.75) tooltip.classList.add("tooltip-right");
      tooltip.textContent = desc;
      el.appendChild(tooltip);

      el.addEventListener("click", () => {
        if (el.classList.contains("active")) {
          this.callbacks.onNodeClick(node);
        }
      });
      this.board.appendChild(el);
    }
  }

  private scrollToCurrent(): void {
    const focusLayer  = this.map.currentNodeId
      ? this.map.getNode(this.map.currentNodeId)?.layer ?? 0
      : 0;
    const viewportH   = this.scroll.clientHeight || 500;
    const gaps        = LAYER_COUNT - 1;
    const rawSpacing  = (viewportH - TOP_PADDING - BOTTOM_PADDING) / gaps;
    const spacing     = Math.min(MAX_SPACING, Math.max(MIN_SPACING, rawSpacing));
    const layerPixelY = TOP_PADDING + focusLayer * spacing;
    this.scroll.scrollTop = Math.max(0, layerPixelY - viewportH / 2 + 30);
  }
}
